import { useId, useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { inventoryApi } from '../../api/inventory-api-instance';
import { ApiError, ConflictError, ForbiddenError, NotFoundError } from '../../api/errors';
import { PartialFailureNotice } from '../../components/PartialFailureNotice';
import type { ClearableSiteField, Site, SiteContactPatch } from '../../domain/inventory';
import {
  buildSiteContactPatch,
  formatSiteAddress,
  SITE_CONTACT_FIELD_LABELS,
  siteContactDraft,
  validateSiteContactDraft,
  type SiteContactDraft,
  type SiteContactDraftField,
} from '../../domain/siteContact';
import { assertSiteSaved, SiteNotSavedError } from '../../domain/siteSaveCheck';
import { siteLabel } from '../../lib/site-label';

interface EditSiteContactDrawerProps {
  site: Site;
  onClose: () => void;
}

const CLEAR_LABELS: Record<ClearableSiteField, string> = {
  address: 'address',
  orderedSystems: 'ordered systems',
};

function saveErrorMessage(error: unknown): string {
  if (error instanceof SiteNotSavedError) {
    const labels = error.fields.map((field) => CLEAR_LABELS[field]).join(', ');
    return `The server didn’t save the ${labels}, so nothing was changed. Please try again later.`;
  }
  if (error instanceof ForbiddenError) {
    return 'You do not have permission to edit this site’s address.';
  }
  if (error instanceof NotFoundError) {
    return 'This site no longer exists. Close this form and reload the page.';
  }
  if (error instanceof ApiError && error.code === 'UNAUTHENTICATED') return error.message;
  return 'The address could not be saved. Check the fields and try again.';
}

function storedValue(site: Site, field: ClearableSiteField): string {
  switch (field) {
    case 'address':
      return formatSiteAddress(site.address) ?? '';
    case 'orderedSystems':
      return site.orderedSystems === undefined ? '' : String(site.orderedSystems);
  }
}

/**
 * Edits a site's shipping address. The people to ship to are the site's
 * Receiving and Operations contacts (tidaltech/koi#8359). Every save sends the version
 * the form was loaded with; a stale version shows the server's values next to
 * the draft instead of overwriting another person's change. Removing a stored
 * value asks for confirmation first.
 */
export function EditSiteContactDrawer({ site, onClose }: EditSiteContactDrawerProps) {
  const queryClient = useQueryClient();
  const formId = useId();
  const [baseSite, setBaseSite] = useState(site);
  const [draft, setDraft] = useState<SiteContactDraft>(() => siteContactDraft(site));
  const [showErrors, setShowErrors] = useState(false);
  const [pendingPatch, setPendingPatch] = useState<SiteContactPatch | null>(null);
  const [conflict, setConflict] = useState<{ latest: Site } | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [unknownOutcome, setUnknownOutcome] = useState(false);

  const errors = validateSiteContactDraft(draft);
  const patch = buildSiteContactPatch(baseSite, baseSite.version, draft);

  const mutation = useMutation({
    // An OK alone doesn't mean it was kept: see SiteNotSavedError.
    mutationFn: async (next: SiteContactPatch) =>
      assertSiteSaved(next, await inventoryApi.updateSiteContact(next)),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['sites'] });
      onClose();
    },
    onError: (error: unknown) => {
      if (error instanceof ConflictError) {
        inventoryApi
          .listSites(site.customerId)
          .then((sites) => {
            const latest = sites.find((candidate) => candidate.id === site.id);
            if (latest) setConflict({ latest });
            else setFormError(saveErrorMessage(new NotFoundError('site not found')));
          })
          .catch(() =>
            setFormError(
              'This site changed after it was loaded, and the latest version could not be fetched.',
            ),
          );
        return;
      }
      if (error instanceof ApiError && error.code === 'NETWORK') {
        setUnknownOutcome(true);
        return;
      }
      setFormError(saveErrorMessage(error));
    },
  });

  function update(field: SiteContactDraftField, value: string): void {
    setDraft((current) => ({ ...current, [field]: value }));
  }

  function save(next: SiteContactPatch): void {
    setFormError(null);
    setUnknownOutcome(false);
    setPendingPatch(null);
    mutation.mutate(next);
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setShowErrors(true);
    if (Object.keys(errors).length > 0 || !patch) return;
    if (patch.clear && patch.clear.length > 0) {
      setPendingPatch(patch);
      return;
    }
    save(patch);
  }

  function fieldProps(field: SiteContactDraftField) {
    const error = showErrors ? errors[field] : undefined;
    return {
      value: draft[field],
      onChange: (event: { target: { value: string } }) => update(field, event.target.value),
      'aria-invalid': error ? true : undefined,
      'aria-describedby': error ? `${formId}-${field}-error` : undefined,
      maxLength: 400,
    };
  }

  function fieldError(field: SiteContactDraftField) {
    const error = showErrors ? errors[field] : undefined;
    return error ? (
      <span className="field-error" id={`${formId}-${field}-error`}>
        {error}
      </span>
    ) : null;
  }

  if (conflict) {
    const latestDraft = siteContactDraft(conflict.latest);
    const fields = (Object.keys(draft) as SiteContactDraftField[]).filter(
      (field) =>
        draft[field].trim() !== siteContactDraft(baseSite)[field].trim() ||
        latestDraft[field].trim() !== siteContactDraft(baseSite)[field].trim(),
    );
    return (
      <div className="drawer-overlay" role="presentation" onClick={onClose}>
        <div
          className="drawer conflict-drawer"
          role="dialog"
          aria-label="Version conflict"
          onClick={(event) => event.stopPropagation()}
        >
          <h2>This site changed while you were editing</h2>
          <p className="conflict-summary">
            Loaded version {baseSite.version} · current version {conflict.latest.version}
            {conflict.latest.lastModifiedBy
              ? ` · changed by ${conflict.latest.lastModifiedBy}`
              : ''}
          </p>
          {fields.length > 0 ? (
            <table className="conflict-table">
              <thead>
                <tr>
                  <th scope="col">Field</th>
                  <th scope="col">Server</th>
                  <th scope="col">Your draft</th>
                </tr>
              </thead>
              <tbody>
                {fields.map((field) => (
                  <tr key={field}>
                    <td>{SITE_CONTACT_FIELD_LABELS[field]}</td>
                    <td>{latestDraft[field] || '—'}</td>
                    <td className="conflict-draft-cell">{draft[field].trim() || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
          <div className="conflict-actions">
            <button type="button" onClick={onClose}>
              Discard my draft
            </button>
            <button
              type="button"
              onClick={() => {
                setBaseSite(conflict.latest);
                setDraft(siteContactDraft(conflict.latest));
                setConflict(null);
                void queryClient.invalidateQueries({ queryKey: ['sites'] });
              }}
            >
              Keep server values
            </button>
            <button
              type="button"
              className="primary"
              onClick={() => {
                // Rebase the draft onto the latest version and review it
                // again before saving, rather than resubmitting blindly.
                setBaseSite(conflict.latest);
                setConflict(null);
                void queryClient.invalidateQueries({ queryKey: ['sites'] });
              }}
            >
              Review my draft on version {conflict.latest.version}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (pendingPatch) {
    const cleared = pendingPatch.clear ?? [];
    return (
      <div className="drawer-overlay centered-overlay" role="presentation" onClick={onClose}>
        <div
          className="confirmation-dialog"
          role="alertdialog"
          aria-labelledby={`${formId}-confirm-title`}
          aria-describedby={`${formId}-confirm-body`}
          onClick={(event) => event.stopPropagation()}
        >
          <div className="drawer-heading">
            <h2 id={`${formId}-confirm-title`}>
              Remove {cleared.map((field) => CLEAR_LABELS[field]).join(' and ')}?
            </h2>
          </div>
          <div className="warning-callout" id={`${formId}-confirm-body`}>
            <p>
              This removes the following from {siteLabel(baseSite)}. The site itself is kept, and
              the change is recorded in its audit history.
            </p>
            <ul>
              {cleared.map((field) => (
                <li key={field}>
                  <strong>{CLEAR_LABELS[field]}:</strong> {storedValue(baseSite, field)}
                </li>
              ))}
            </ul>
          </div>
          <div className="dialog-actions">
            <button type="button" autoFocus onClick={() => setPendingPatch(null)}>
              Go back
            </button>
            <button type="button" className="button-danger" onClick={() => save(pendingPatch)}>
              Remove and save
            </button>
          </div>
        </div>
      </div>
    );
  }

  const hasAddress = [draft.line1, draft.line2, draft.postcode, draft.city].some(
    (value) => value.trim() !== '',
  );

  return (
    <div className="drawer-overlay" role="presentation" onClick={onClose}>
      <form
        className="drawer"
        aria-label="Edit site address"
        onClick={(event) => event.stopPropagation()}
        onSubmit={handleSubmit}
        noValidate
      >
        <div className="drawer-heading">
          <div>
            <span className="step-label">
              {siteLabel(baseSite)} · version {baseSite.version}
            </span>
            <h2>Address</h2>
          </div>
          <button type="button" className="icon-button" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <p className="drawer-version-note">
          Used as the default destination when shipping to this site. Conflicts require review.
        </p>

        {formError ? (
          <p role="alert" className="form-error">
            {formError}
          </p>
        ) : null}
        {unknownOutcome ? (
          <PartialFailureNotice
            onCheckCurrentState={() => {
              void queryClient.invalidateQueries({ queryKey: ['sites'] });
              onClose();
            }}
          />
        ) : null}

        <fieldset className="drawer-section">
          <legend>Address</legend>
          <label>
            {SITE_CONTACT_FIELD_LABELS.line1}
            <input type="text" autoComplete="off" autoFocus {...fieldProps('line1')} />
            {fieldError('line1')}
          </label>
          <label>
            {SITE_CONTACT_FIELD_LABELS.line2}
            <input type="text" autoComplete="off" {...fieldProps('line2')} />
            {fieldError('line2')}
          </label>
          <div className="form-row">
            <label>
              {SITE_CONTACT_FIELD_LABELS.postcode}
              <input type="text" autoComplete="off" {...fieldProps('postcode')} />
              {fieldError('postcode')}
            </label>
            <label>
              {SITE_CONTACT_FIELD_LABELS.city}
              <input type="text" autoComplete="off" {...fieldProps('city')} />
              {fieldError('city')}
            </label>
          </div>
          <p className="drawer-version-note">
            Country comes from the site’s country label
            {baseSite.labels?.country ? ` (${baseSite.labels.country})` : ''}.
          </p>
          <button
            type="button"
            className="link-button"
            onClick={() =>
              setDraft((current) => ({ ...current, line1: '', line2: '', postcode: '', city: '' }))
            }
            disabled={!hasAddress}
          >
            Clear address
          </button>
        </fieldset>

        <div className="drawer-actions">
          <button type="button" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </button>
          <button type="submit" className="primary" disabled={mutation.isPending || !patch}>
            {mutation.isPending ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </form>
    </div>
  );
}
